import questionsData from '../data/questions.json';
import psychometricsData from '../data/psychometrics.json';
import { deepClean } from './runtimeValidation';
import { dndService, ClusterInfo } from '../services/dndService';
import { logger } from './logger';
import { normalizeCamel, normalizeAnswer, clampToRange, createNormalizedScope } from './normalization';
import { Errors, safeExecute, assert, assertExists, PsycoError } from './errorHandling';

/**
 * Validates the syntax of a mathematical/logical expression
 * @param expr The expression to validate
 * @returns boolean indicating if the expression has valid syntax
 */
function isValidExpression(expr: string): boolean {
  // Check for balanced parentheses
  const stack: string[] = [];
  for (let i = 0; i < expr.length; i++) {
    const char = expr[i];
    if (char === '(') {
      stack.push(char);
    } else if (char === ')') {
      if (stack.length === 0) return false;
      stack.pop();
    }
  }
  
  if (stack.length > 0) return false; // Unbalanced parentheses
  
  // Check for valid operator sequences
  const operatorSequence = /([+\-*/%<>=!&|]{2,})/g;
  const matches = expr.match(operatorSequence);
  if (matches) {
    // Allow only valid operator combinations like '>=', '<=', '==', '!=', '&&', '||'
    const validOperators = ['>=', '<=', '==', '!=', '&&', '||', '**'];
    for (const match of matches) {
      if (!validOperators.includes(match) && match.length > 2) {
        return false;
      }
    }
  }
  
  return true;
}


/**
 * Safely evaluate a mathematical expression with proper error handling
 * @param condition - The expression to evaluate
 * @param scope - Variables available in the expression
 * @param defaultValue - Default value if evaluation fails
 * @returns The result of evaluation or default value
 */
const safeEvaluate = (condition: string, scope: Record<string, any>, defaultValue: any = 0) => {
  try {
    // Input validation
    if (!condition || typeof condition !== 'string') {
      console.warn('[safeEvaluate] Invalid condition:', condition);
      return defaultValue;
    }

    if (!scope || typeof scope !== 'object') {
      console.warn('[safeEvaluate] Invalid scope:', scope);
      return defaultValue;
    }

    // Clean the formula from invisible characters and normalize
    let cleanFormula = condition
      .replace(/[\u200B-\u200D\uFEFF\r\n]/g, '') // Remove invisible characters
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();

    if (!cleanFormula) {
      console.warn('[safeEvaluate] Empty condition after cleaning');
      return defaultValue;
    }

    // Convert PascalCase variables to camelCase
    cleanFormula = cleanFormula.replace(/\b([A-Z][a-z0-9]+)/g, (m) => 
      m.charAt(0).toLowerCase() + m.slice(1)
    );

    // Security check: allow mathematical expressions with variables, numbers, and common operators
    // This pattern allows:
    // - Variables: [a-zA-Z_][a-zA-Z0-9_]*
    // - Numbers: \d*\.?\d+
    // - Operators: + - * / % < > = ! & | ( )
    // - Whitespace: \s
    const allowedPattern = /^[\s\w\d.+\-*/%<>=!&|()]+$/;
    
    // Check for potentially dangerous patterns
    const dangerousPatterns = [
      /\b(?:eval|new Function|Function\(|\bimport\b|\brequire\b|\bprocess\b|\bfs\b|\bfile\b)/,
      /[;{}`'"\[\]\\]/,
    ];
    
    const hasDangerousPattern = dangerousPatterns.some(pattern => pattern.test(cleanFormula));
    if (hasDangerousPattern) {
      console.warn('[safeEvaluate] Rejected potentially dangerous formula:', cleanFormula);
      return defaultValue;
    }
    
    // Basic syntax validation - check balanced parentheses and valid operator usage
    if (!isValidExpression(cleanFormula)) {
      console.warn('[safeEvaluate] Invalid expression syntax:', cleanFormula);
      return defaultValue;
    }

    // Prepare function parameters
    const keys = Object.keys(scope);
    const values = Object.values(scope);

    // Create the evaluation function
    // eslint-disable-next-line no-new-func
    const evalFunction = new Function(...keys, `return ${cleanFormula};`);
    
    // Execute with timeout protection
    const result = evalFunction(...values);

    // Validate result
    if (typeof result === 'number' && !isNaN(result) && isFinite(result)) {
      return result;
    } else if (typeof result === 'boolean') {
      return result;
    } else {
      console.warn('[safeEvaluate] Invalid result type:', typeof result, result);
      return defaultValue;
    }

  } catch (error) {
    console.error('[safeEvaluate] Error evaluating condition:', {
      condition,
      error: error.message,
      scope: Object.keys(scope)
    });
    return defaultValue;
  }
};

export const calculateScores = (answers: Record<string, unknown>, questionsOrMap: Map<string, any> | any[]): Record<string, number> => {
  // DEBUG: входные данные
  logger.debugAnswers(answers, { module: 'CALCULATIONS', function: 'calculateScores' });
  
  // Универсально: если передан массив — строим Map
  let questionsMap: Map<string, any>;
  if (Array.isArray(questionsOrMap)) {
    questionsMap = new Map(questionsOrMap.map(q => [q.id || q.questionId, q]));
  } else {
    questionsMap = questionsOrMap;
  }
  
  if (!questionsMap || typeof questionsMap.get !== 'function' || questionsMap.size === 0) {
    throw Errors.INVALID_SCORES({ questionsMapSize: questionsMap?.size });
  }
  
  if (!answers || typeof answers !== 'object' || !Object.keys(answers).length) {
    return {};
  }

  // --- Новый алгоритм расчёта ---
  const scaleSums: Record<string, number> = {};
  const scaleWeightSums: Record<string, number> = {};

  for (const [questionId, answerRaw] of Object.entries(answers)) {
    const question = questionsMap.get(questionId);
    if (!question || !question.weights) continue;
    
    // Используем утилиту нормализации ответов
    const answer = normalizeAnswer(answerRaw);
    if (answer === null || isNaN(answer)) continue;
    
    // Ограничиваем диапазон
    const clampedAnswer = clampToRange(answer);
    
    for (const [scale, weight] of Object.entries(question.weights)) {
      const w = typeof weight === 'number' ? weight : Number(weight);
      if (isNaN(w)) continue;
      scaleSums[scale] = (scaleSums[scale] || 0) + clampedAnswer * w;
      scaleWeightSums[scale] = (scaleWeightSums[scale] || 0) + Math.abs(w);
    }
  }
  
  // DEBUG: промежуточные суммы
  logger.debugScaleSums(scaleSums, { module: 'CALCULATIONS', function: 'calculateScores' });
  logger.debugScaleWeightSums(scaleWeightSums, { module: 'CALCULATIONS', function: 'calculateScores' });

  // --- Нормализация ---
  const finalScores: Record<string, number> = {};
  for (const scale in scaleSums) {
    const denom = scaleWeightSums[scale];
    const norm = denom ? (scaleSums[scale] / denom) : 0.0;
    finalScores[scale] = (norm + 1) * 50;
  }
  
  logger.debugFinalScores(finalScores, { module: 'CALCULATIONS', function: 'calculateScores' });
  return finalScores;
};

/**
 * @typedef {Object} SessionQuestion
 * @property {string} id
 * @property {any} answer
 * @property {string} [text]
 * @property {string} [questionId]
 * @property {string} [questionText]
 */
/**
 * @param {SessionQuestion[]} sessionQuestions
 * @returns {Promise<import('../types/result_test.schema').ResultTestSchema>}
 */
export async function calculateResults(normalized: any) {
  // Универсальная обработка: если пришёл массив — оборачиваем в объект
  if (Array.isArray(normalized)) {
    normalized = { sessionQuestions: normalized };
  }
  
  // Extract extended metrics if provided
  const extendedMetrics = normalized.extended_metrics || {};
  
  // DEBUG: входные данные
  logger.debugSessionQuestions(normalized.sessionQuestions, { module: 'CALCULATIONS', function: 'calculateResults' });
  
  if (typeof window !== 'undefined' && window.logPsycoDebug) {
    window.logPsycoDebug({
      step: 'calculateResults:entry',
      sessionQuestions: normalized.sessionQuestions,
      allQuestions: Array.isArray(questionsData.questions) ? questionsData.questions : undefined
    });
  }
  
  if (!Array.isArray(normalized.sessionQuestions) || normalized.sessionQuestions.length === 0) {
    // Все шкалы = 50, attention = 50
    const allScales = [
      'suppression','dissociation','empathy','psychopathy','internalDissonance','affectiveIsolationScale','emotionalClimate','masking','affectiveDisruptionIndex','childhoodAlienation','ace','flattening','lie','attention'
    ];
    const scores: Record<string, number> = {};
    for (const scale of allScales) scores[scale] = 50;
    
    // Calculate cluster with DnD archetype using the service directly
    const cluster = dndService.calculateCluster(scores, extendedMetrics);
    
    return {
      r_metric: 0,
      scores,
      profiles: [],
      ratio: 0,
      rawScores: {},
      mappedScores: {},
      extended_metrics: extendedMetrics,
      demographics: normalized.demographics || {},
      timestamp: normalized.timestamp || Date.now(),
      resultId: normalized.resultId || '',
      sessionQuestions: [],
      cluster,
    };
  }
  
  const answers = {};
  for (const q of normalized.sessionQuestions) {
    if (q.id && q.answer !== undefined) {
      answers[q.id] = q.answer;
    }
  }
  
  // DEBUG: answers для расчёта
  logger.debugAnswers(answers, { module: 'CALCULATIONS', function: 'calculateResults' });
  
  // --- Новый блок: диагностика ---
  const hasAnyRealAnswer = Object.values(answers).some(v => v !== undefined && v !== null && v !== '' && v !== 'Не отвечал');
  
  try {
      // --- Используем window.questions и window.psychometrics, если они есть ---
      const questionsArray = (typeof window !== 'undefined' && window.questions) ? window.questions : questionsData.questions;
      const psychometrics = (typeof window !== 'undefined' && window.psychometrics) ? window.psychometrics : psychometricsData;
      const questionsMap = new Map();
      if (Array.isArray(questionsArray)) {
          for (const q of questionsArray) {
              questionsMap.set(q.id, q);
          }
      }
    
      // --- Проверяем: есть ли в базе вопросы с ненулевым весом по основной шкале (в т.ч. Attention) ---
      const mainScales = [
        'Suppression','Dissociation','Empathy','Psychopathy','InternalDissonance','AffectiveIsolationScale','EmotionalClimate','Masking','AffectiveDisruptionIndex','ChildhoodAlienation','ACE','Flattening','Lie','Attention'
      ];
      const hasAnyWeightedInBase = Array.from(questionsMap.values()).some(q => q && q.weights && Object.keys(q.weights).some(scale => mainScales.includes(scale) && q.weights[scale] && q.weights[scale] !== 0));
    
      if (!hasAnyWeightedInBase) {
      throw Errors.NO_VALID_SCORES({ mainScales });
      }
    
      // 1. Calculate all raw scores from answers
      const rawScores: Record<string, number> = calculateScores(answers, questionsMap);
      if ((rawScores as any).__onlyControl) {
      throw Errors.NO_VALID_SCORES({ rawScores });
    }
    
    // Используем утилиту нормализации ключей
      const camelScores: Record<string, number> = {};
      for (const key in rawScores) {
        camelScores[normalizeCamel(key)] = rawScores[key];
      }
    
      // --- Явно нормализую все ключи к camelCase для safeEvaluate ---
    const camelCaseScope = createNormalizedScope(camelScores, {}, mainScales.map(s => normalizeCamel(s)));
    
    // 2. Calculate R-metric
    const r_metric_value = safeEvaluate(psychometrics.r_metric.formula, camelCaseScope, 0);
    
    // --- Формируем scope только с camelCase ключами ---
    const scope: Record<string, number> = { ...camelCaseScope, r_metric: r_metric_value };
    
    // 3. extended_metrics: теперь считаем динамически по формуле из psychometricsData.extended_metrics
    const extended_metrics: Record<string, any> = {};
    if (psychometrics.extended_metrics) {
      for (const key in psychometrics.extended_metrics) {
        const meta = psychometrics.extended_metrics[key];
        if (meta && meta.formula) {
          // Проверяем, что все переменные для формулы есть в scope
          if (Array.isArray(meta.variables)) {
            for (const varName of meta.variables) {
              if (!(varName in scope)) {
                throw Errors.CALCULATION_FAILED({ 
                  missingVariable: varName, 
                  formula: meta.formula,
                  availableVariables: Object.keys(scope)
                });
              }
            }
          }
          let value = safeEvaluate(meta.formula, scope, undefined);
          if (value === undefined || value === null || isNaN(value)) {
            throw Errors.CALCULATION_FAILED({ 
              invalidResult: value, 
              formula: meta.formula 
            });
          }
          extended_metrics[key] = value;
        } else {
          throw Errors.CALCULATION_FAILED({ 
            missingFormula: key 
          });
        }
      }
    }
    
    // Добавляем extended_metrics в scope для оценки профилей
    const extendedScope = { ...scope, ...extended_metrics };
    
      // 4. Determine psychological profiles
      const profileNames = (() => {
        const matchedProfileKeys: string[] = [];
        for (const key in psychometrics.profiles) {
          const profile = psychometrics.profiles[key];
        if (safeEvaluate(profile.criteria, extendedScope)) {
            matchedProfileKeys.push(key);
          }
        }
        return matchedProfileKeys;
      })();
    
      // 5. Финальные scores — только реально рассчитанные шкалы с ненулевым значением (attention — всегда, если есть)
      const allCamelScales = Array.from(new Set([...Object.keys(camelScores), ...Object.keys(rawScores)]));
      if (!allCamelScales.includes('attention') && typeof camelScores['attention'] !== 'undefined') allCamelScales.push('attention');
      const finalCamelScores: Record<string, number> = {};
      for (const key of allCamelScales) {
          const value = Math.round(camelScores[key] ?? 0);
          // Добавляем только если реально был ответ (значение не 0), либо это attention
          if ((value !== 0 && !isNaN(value)) || key === 'attention') {
              finalCamelScores[key] = value;
          }
      }
    
      const result: CalculatedResults = {
          scores: camelCaseScope,
          profiles: profileNames,
          r_metric: typeof r_metric_value === 'number' && isFinite(r_metric_value) ? 
                   parseFloat(r_metric_value.toFixed(2)) : 0,
          ratio: typeof r_metric_value === 'number' && isFinite(r_metric_value) ? 
                Math.round(r_metric_value) : 0,
          rawScores: rawScores as Record<string, number>,
          mappedScores: camelScores as Record<string, number>,
          extended_metrics: extended_metrics as Record<string, any>,
          demographics: normalized.demographics || {},
          timestamp: normalized.timestamp || Date.now(),
          resultId: normalized.resultId || '',
          sessionQuestions: Array.isArray(normalized.sessionQuestions) ? normalized.sessionQuestions : [],
      };

      // Calculate DnD archetype and cluster
      const cluster = dndService.calculateCluster(finalCamelScores, extended_metrics);
      result.cluster = cluster;

      // После формирования result:
      // --- Диагностика: если есть реальные ответы, а все значения scores = 0 или пусто ---
      if (hasAnyRealAnswer) {
        const allScoreValues = Object.values(result.scores || {});
        const allZeroOrEmpty = allScoreValues.length === 0 || allScoreValues.every(v => v === 0 || v === undefined || isNaN(v));
        // Если есть только attention: 0 — это валидно для attentionControl тестов
        if (allZeroOrEmpty && !(Object.keys(result.scores).length === 1 && 'attention' in result.scores)) {
        logger.error('Реальные ответы есть, но все значения scores = 0 или пусто!', {
          module: 'CALCULATIONS',
          function: 'calculateResults'
        }, {
          sessionQuestions: normalized.sessionQuestions,
          answers,
          scores: result.scores
        });
        throw Errors.NO_VALID_SCORES({ 
          hasRealAnswers: true,
          allScoresZero: true,
          sessionQuestions: normalized.sessionQuestions,
          answers
        });
      }
    }
    
      // --- Гарантируем, что все обязательные поля есть ---
      const requiredFields = {
        demographics: {},
        rawScores: {},
        mappedScores: {},
        extended_metrics: {}
      };
      for (const key in requiredFields) {
        if (!(key in result)) result[key] = requiredFields[key];
      }
    
      return deepClean(result);
  } catch (error) {
    // Обработка ошибок с fallback значениями
    if (error instanceof PsycoError) {
      throw error;
    }
    
    logger.error('Unexpected error in calculateResults', {
      module: 'CALCULATIONS',
      function: 'calculateResults'
    }, error);
    
    // Fallback: все шкалы = 50
    const allScales = [
      'suppression','dissociation','empathy','psychopathy','internalDissonance','affectiveIsolationScale','emotionalClimate','masking','affectiveDisruptionIndex','childhoodAlienation','ace','flattening','lie','attention'
    ];
    const scores: Record<string, number> = {};
    for (const scale of allScales) scores[scale] = 50;
    
    // Calculate DnD archetype for fallback case using the service directly
    const cluster = dndService.calculateCluster(scores, {});
    
    return {
      r_metric: 0,
      scores,
      profiles: [],
      ratio: 0,
      rawScores: {},
      mappedScores: {},
      extended_metrics: {},
      demographics: normalized.demographics || {},
      timestamp: normalized.timestamp || Date.now(),
      resultId: normalized.resultId || '',
      sessionQuestions: Array.isArray(normalized.sessionQuestions) ? normalized.sessionQuestions : [],
      cluster,
    };
  }
}

// Re-export the DnD service and cluster calculation for backward compatibility
export { 
  dndService, 
  calculateCluster,
  DEFAULT_ARCHETYPE,
  DEFAULT_CLUSTER,
  ERROR_CLUSTER
} from '../services/dndService';

// Legacy function for backward compatibility
export function loadDndStructure() {
  console.warn('loadDndStructure is deprecated. Use dndService from services/dndService instead.');
  return dndService['structure'];
}

// Функция getPsychometrics теперь просто возвращает импортированный объект
export async function getPsychometrics(): Promise<any> {
    return psychometricsData;
}

export { safeEvaluate };

export function toPascalCase(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

// Re-export types from dnd service
export type { DndArchetype, DndStructure } from '../services/dndService';

// Session question interface
export interface SessionQuestion {
  id: string;
  answer: unknown;
  text?: string;
  questionId?: string;
  questionText?: string;
}

// Calculation results interface
export interface CalculatedResults {
  scores: Record<string, number>;
  profiles: string[];
  r_metric: number;
  cluster?: ClusterInfo;
  [key: string]: any;
}

export type CalculationResult = CalculatedResults;